import { ApiProblem } from '../api/client.js';
import { getCase, patchCase, type AuditOverride } from '../api/cases.js';
import type { Case } from '../domain/initial-cases.js';

export interface UpdateOptions {
  toast?: (msg: string) => void;
}

export type CasePatch = Partial<Case> & { action?: AuditOverride };

// M-5: the result carries the single resolved case, not a rebuilt cases
// array — the caller merges it into CURRENT state with a functional update,
// so an in-flight PATCH can no longer clobber concurrent addCase /
// mergeLocalCase / list-fetch mutations that landed mid-flight.
export interface UpdateResult {
  /** Server truth for this case: the PATCHed row, the refetched row on 409,
   *  or `current` unchanged on other errors (rollback). */
  case: Case;
  outcome: 'updated' | 'conflict' | 'error';
}

/**
 * PATCH a case with If-Match optimistic concurrency. On 409 (version
 * mismatch), refetch the canonical state and surface a toast so the
 * caller can prompt the user. On other errors, toast and return the
 * pre-patch case (rollback).
 *
 * #834 — a version mismatch is the 409 that carries `currentVersion`. The
 * route answers 409 for other conflicts too (an LOA verify naming a letter the
 * customer has since replaced, a change to the case's customer), and those
 * are refusals with a reason to show, not "another user updated this case".
 */
export async function updateCaseViaApi(
  current: Case,
  patch: CasePatch,
  opts: UpdateOptions = {},
): Promise<UpdateResult> {
  try {
    const updated = await patchCase(current.id, current.version, patch);
    return { case: updated, outcome: 'updated' };
  } catch (err) {
    if (err instanceof ApiProblem && err.status === 409 && err.extensions.currentVersion !== undefined) {
      const fresh = await getCase(current.id);
      opts.toast?.('Another user updated this case — showing the latest version.');
      return { case: fresh, outcome: 'conflict' };
    }
    // #690 — the DETAIL, falling back to the title. An ApiProblem's `message`
    // is its `title`, and the API's titles are status phrases: 'Forbidden',
    // 'Conflict', 'Unprocessable Entity'. The sentence a user can act on is
    // always in `detail` ("…removes no evidence record. The audit trail would
    // claim a deletion that did not happen — send the kyc object with the
    // record left out…"), so toasting the message threw away the whole
    // explanation and left the analyst with "Update failed: Unprocessable
    // Entity". The example moved with #719: the refusal quoted here used to be
    // the admin-only 403, which the route no longer produces.
    const reason = err instanceof ApiProblem
      ? (err.detail ?? err.title)
      : err instanceof Error ? err.message : String(err);
    opts.toast?.(`Update failed: ${reason}`);
    return { case: current, outcome: 'error' };
  }
}
